// La barra fija al pie de MOBILE en el modo Modelo: los ajustes de la tanda y el Generar.
//
// Es la MISMA anatomía que el pie de la columna de desktop (`PanelCompacto`)
// —ADR-2026-09-03-modelo-mobile-toma-el-pie-de-desktop—: la fila de Calidad + Formato
// arriba, y abajo el contador pegado a la izquierda del botón. Antes acá había un botón
// resumen ("1080p · Posteo (4:5) · 1 imagen") que abría un bottom sheet con los cuatro
// ajustes: para ver qué estaba elegido —o para cambiar una cosa— había que abrir una capa
// más, y el mismo pie se veía distinto en las dos pantallas.
//
// Fondo NO está acá, igual que en desktop: es una decisión con foto y vive como card en la
// pantalla de Modelo y pose.
//
// Vive en su propio archivo porque la usan TRES pantallas —productos, modelo y pose, y el
// selector de productos full-screen— y en las tres tiene que ser la misma
// (ADR-2026-08-24-modelo-v2-mobile-barra-en-las-dos-pantallas).

import React from 'react';
import {
  CalidadSegmentada,
  CeldaAjuste,
  ValorConChevron,
  type CalidadImagen,
} from '../components/AjustesRapidosProducto';
import { ContadorImagenes } from '../components/ContadorImagenes';

export interface BarraInferiorMobileProps {
  calidad: CalidadImagen;
  onCalidadChange: (v: CalidadImagen) => void;
  formatoLabel: string;
  /** Abre el picker de formato (el sheet de siempre). */
  onOpenFormato: () => void;
  /** Cantidad de imágenes de la tanda: el contador pegado al Generar. */
  cantidad: number;
  cantidadMax: number;
  onCantidadChange: (v: number) => void;
  /**
   * ¿Se dibuja el contador? (`TandaModelo.contadorAplica`)
   *
   * Con 2+ poses NO: la tanda la fija el catálogo de poses —una imagen por cada una— y
   * un contador que no multiplica nada es un control muerto que además contradice al
   * botón (ADR-2026-09-04-con-varias-poses-el-contador-se-esconde). Default `true`
   * para los call-sites que no deciden nada de esto.
   */
  contadorVisible?: boolean;
  /** Cuántas imágenes salen — va en el texto del CTA. */
  imagenesDeLaTanda: number;
  creditCost: number | null;
  onGenerate: () => void;
  /** Con algo sin elegir el CTA se apaga y esto dice qué falta, DEBAJO del botón. */
  falta?: string;
  /**
   * La línea que explica la tanda, en el MISMO renglón que `falta` y debajo del botón.
   * `falta` gana cuando están las dos: lo que bloquea se lee antes que lo que saldría.
   *
   * La usa Automática, que es el único modo cuya combinación no es obvia —con varios
   * productos NO sale una imagen por producto, sale UNA con todos juntos— y cuya única
   * pantalla es ésta (ADR-2026-09-23-automatico-en-mobile-tiene-su-generar). Los montajes
   * de Modelo no la pasan: ahí la cuenta ya está en el texto del CTA.
   */
  resumen?: string;
  /**
   * Texto del CTA. Default: "Generar N imágenes".
   *
   * La pantalla de productos usa "Siguiente": ahí el botón no dispara la tanda, devuelve
   * al paso donde se eligen Modelo, Pose y el fondo.
   */
  ctaLabel?: string;
  /** Esconde la pastilla de créditos — no hay costo que anunciar si no se genera. */
  ocultarCosto?: boolean;
  /**
   * La barra es SÓLO el botón y su nota: sin Calidad, sin Formato y sin contador —
   * ADR-2026-09-04-los-ajustes-de-mobile-aparecen-en-el-paso-2.
   *
   * Es lo que corresponde en los pasos donde todavía se está eligiendo QUÉ generar (el
   * paso 1 del modo y la pantalla de selección de productos): ahí esos tres controles
   * ajustan una tanda que todavía no existe, y ocupan el tercio inferior de la pantalla
   * compitiendo con la grilla, que es lo único que hay que mirar. Reaparecen en el paso 2,
   * pegados al Generar, que es donde la decisión ya está tomada y el número cuesta.
   */
  soloCta?: boolean;
}

export const BarraInferiorMobile: React.FC<BarraInferiorMobileProps> = ({
  calidad,
  onCalidadChange,
  formatoLabel,
  onOpenFormato,
  cantidad,
  cantidadMax,
  onCantidadChange,
  contadorVisible = true,
  imagenesDeLaTanda,
  creditCost,
  onGenerate,
  falta,
  resumen,
  ctaLabel,
  ocultarCosto = false,
  soloCta = false,
}) => (
  <div
    // Anclada al fondo del sheet: el sobrante se lo reparten los `m*-auto`
    // del grupo de arriba, así que acá no hace falta ningún empuje propio.
    className="shrink-0 border-t border-black/[0.06] bg-white px-4 pt-3 flex flex-col gap-2.5"
    style={{
      paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
    }}
  >
    {/* Calidad y Formato, a la vista: las mismas celdas que dibuja el pie de desktop
        (`AjustesRapidosProducto`), así que los dos controles se ven igual en las dos
        pantallas (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos). */}
    {!soloCta && (
      <div className="grid grid-cols-2 gap-2">
        <CeldaAjuste etiqueta="Calidad">
          <CalidadSegmentada calidad={calidad} onChange={onCalidadChange} />
        </CeldaAjuste>
        <CeldaAjuste etiqueta="Formato" onClick={onOpenFormato}>
          <ValorConChevron texto={formatoLabel} />
        </CeldaAjuste>
      </div>
    )}

    <div className="flex items-stretch gap-2">
      {/* La cantidad, PEGADA al Generar: es el único ajuste que cambia lo que dice el
          botón y lo que cuesta la tanda, así que se decide con el número y el precio a la
          vista. `[&>span]:mt-0` neutraliza el `mt-1` que `ContadorImagenes` trae de cuando
          vivía debajo de la etiqueta de una celda. */}
      {contadorVisible && !soloCta && (
        <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 flex items-center [&>span]:mt-0">
          <ContadorImagenes
            valor={cantidad}
            max={cantidadMax}
            onChange={onCantidadChange}
          />
        </div>
      )}
      <button
        type="button"
        disabled={!!falta}
        onClick={onGenerate}
        className="flex-1 min-w-0 py-3.5 rounded-2xl bg-brand-primary text-white text-[15.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
      >
        {ctaLabel ?? (
          <>
            Generar {imagenesDeLaTanda}{' '}
            {imagenesDeLaTanda === 1 ? 'imagen' : 'imágenes'}
          </>
        )}
        {!ocultarCosto && creditCost != null && creditCost > 0 && (
          <span className="inline-flex items-center gap-1 bg-white text-brand-primary px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-3.5 h-3.5 object-contain"
            />
            {creditCost}
          </span>
        )}
      </button>
    </div>

    {/* El estado va DEBAJO del botón: arriba empujaba al CTA hacia abajo cada vez que
        aparecía o desaparecía —el botón se movía bajo el dedo—. Acá el CTA se queda quieto
        y el mensaje se suma al pie. */}
    {(falta ?? resumen) && (
      <p className="text-[12.5px] text-brand-black/45 text-center">
        {falta ?? resumen}
      </p>
    )}
  </div>
);
